import React from "react";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import { ChatDocumentCapability } from "@shared/api/chatApi";
import { ChatDocumentAttachmentState } from "./useChatDocumentAttachment";

/**
 * 문서(IDP) 첨부 칩/고지 UI(plan §Task 22) — 상태 전 국면(업로드/대기/검사/추출/검증/READY/
 * 실패/제거)을 진행률·기한·복구 동작과 함께 표시한다. READY 전에 성공을 암시하는 문구는 없다.
 * 파일명은 로컬 표시 전용이고, 래스터 완전성 경고와 실패의 한국어 복구 안내는 서버 정규 문구를
 * 그대로 렌더한다(React 텍스트 — HTML 미해석).
 */

/**
 * 파일 선택 accept 목록 — 문서 형식은 capability가 허용할 때만 광고된다.
 * 비활성 회사는 표(xlsx/csv)만 선택 가능하다.
 */
export function attachmentAcceptFor(capability: ChatDocumentCapability | null): string {
  return capability && capability.enabled
    ? ".xlsx,.csv,.pdf,.png,.jpg,.jpeg"
    : ".xlsx,.csv";
}

/** 고정 외부 전송 고지 — 서버 capability의 정확한 문구를 그대로, 모달 없이 상시 노출한다. */
export function ChatbotDocumentDisclosure({
  capability,
}: {
  capability: ChatDocumentCapability | null;
}) {
  if (!capability || !capability.enabled) {
    return null;
  }
  return (
    <div className="chatbot-document-disclosure" role="note">
      {capability.disclosureKo}
    </div>
  );
}

/** UTC 타임스탬프 → 로컬 HH:MM 표시(파싱 실패 시 비표시). */
function localClock(timestamp: string | undefined): string | null {
  if (!timestamp) {
    return null;
  }
  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  const hours = String(parsed.getHours()).padStart(2, "0");
  const minutes = String(parsed.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}

const RASTER_WARNING_KO =
  "스캔 이미지의 전체 품목 행 수는 자동 검증되지 않았습니다. 원본 문서와 함께 확인해 주세요.";

function statusLine(state: ChatDocumentAttachmentState): string {
  switch (state.status) {
    case "IDLE":
      return "";
    case "UPLOADING":
      return "업로드 중…";
    case "PROCESSING": {
      if (state.phase === "QUEUED") {
        const deadline = localClock(state.queueDeadline);
        return deadline ? `분석 대기 중 · 대기 기한 ${deadline}` : "분석 대기 중";
      }
      if (state.phase === "INSPECTING") {
        const deadline = localClock(state.processingDeadline);
        return deadline ? `문서 검사 중 · 처리 기한 ${deadline}` : "문서 검사 중";
      }
      const step = state.phase === "VALIDATING" ? "검증 중" : "분석 중";
      return state.totalPages !== undefined
        ? `${step} · ${state.processedPages}/${state.totalPages}페이지`
        : `${step} · ${state.processedPages}페이지 처리`;
    }
    case "READY":
      return `문서 · ${state.totalPages}페이지 · ${
        state.documentQuality === "COMPLETE" ? "추출 완전" : "검토 필요"
      }`;
    case "FAILED":
      return state.error?.messageKo ?? "문서 처리에 실패했습니다. 다시 업로드해 주세요.";
    case "REMOVING":
      return "제거 중…";
    default: {
      const exhaustive: never = state;
      return exhaustive;
    }
  }
}

export interface ChatbotDocumentChipProps {
  state: ChatDocumentAttachmentState;
  busy: boolean;
  onRemove: () => void;
  onReupload: () => void;
  /** FAILED 칩 닫기 — 로컬 소거만(서버 정리 주장 없음). */
  onDismiss: () => void;
}

export function ChatbotDocumentChip({
  state,
  busy,
  onRemove,
  onReupload,
  onDismiss,
}: ChatbotDocumentChipProps) {
  if (state.status === "IDLE") {
    return null;
  }
  const removable = state.status === "PROCESSING" || state.status === "READY";
  return (
    <div className="chatbot-attachments" data-testid="document-chip">
      <div
        className={`chatbot-attachment chatbot-attachment-document chatbot-attachment-document-${state.status.toLowerCase()}`}
      >
        <AttachFileIcon className="chatbot-attachment-icon" fontSize="small" />
        <div className="chatbot-attachment-meta">
          <span className="chatbot-attachment-name">{state.displayName}</span>
          <span className="chatbot-attachment-sub" data-testid="document-status-line">
            {statusLine(state)}
          </span>
          {state.status === "READY"
            && state.lineCompleteness === "UNKNOWN_RASTER_LINE_COMPLETENESS" && (
            <span className="chatbot-document-warning" data-testid="document-raster-warning">
              {RASTER_WARNING_KO}
            </span>
          )}
        </div>
        {state.status === "FAILED" ? (
          <>
            <button
              aria-label="문서 다시 업로드"
              className="chatbot-attachment-reupload"
              disabled={busy}
              onClick={onReupload}
              type="button"
            >
              다시 업로드
            </button>
            <button
              aria-label="문서 오류 닫기"
              className="chatbot-attachment-remove"
              disabled={busy}
              onClick={onDismiss}
              type="button"
            >
              <CloseIcon fontSize="inherit" />
            </button>
          </>
        ) : (
          <button
            aria-label="문서 첨부 제거"
            className="chatbot-attachment-remove"
            disabled={busy || !removable}
            onClick={onRemove}
            type="button"
          >
            <CloseIcon fontSize="inherit" />
          </button>
        )}
      </div>
    </div>
  );
}
